import { useCallback } from 'react'

import { images } from '@batical/common'
import { sectionColors } from '@batical/design'
import i18n from '@batical/i18n'
import { Box, Card, Typography, styled } from '@mui/material'
import { darken } from '@mui/material/styles'
import { FaChevronRight } from 'react-icons/fa'

type Props = {
  onClick: (code: string) => void
  code: string
}

const ProductImgStyle = styled('img')({
  objectFit: 'contain',
  transition: 'transform 0.3s ease'
})

function initialsFromCode(code: string): string {
  const slug = code.replace(/^S\d+/, '')
  if (slug.length >= 2) return slug.slice(0, 2).toUpperCase()
  return code.slice(0, 2).toUpperCase()
}

/** Fine courbe type « vague » : trait hairline bas-gauche → haut-droite (comme la ref. iOS). */
function SectionTopSheen() {
  return (
    <Box
      aria-hidden
      component="svg"
      viewBox="0 0 200 120"
      preserveAspectRatio="none"
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 2,
        opacity: { xs: 0.52, sm: 0.48 }
      }}>
      <path
        d="M 6 108 Q 88 52, 196 6"
        fill="none"
        stroke="rgba(255,255,255,0.72)"
        strokeWidth={1.05}
        strokeLinecap="round"
        vectorEffect="nonScalingStroke"
      />
      <path
        d="M 14 102 Q 92 58, 188 14"
        fill="none"
        stroke="rgba(255,255,255,0.22)"
        strokeWidth={0.65}
        strokeLinecap="round"
        vectorEffect="nonScalingStroke"
      />
    </Box>
  )
}

const SectionCard = ({ onClick, code }: Props) => {
  const handleExpandClick = useCallback(() => {
    onClick(code)
  }, [code, onClick])

  const imgSrc = images[code]
  const tint = sectionColors[code]
  const base = darken(tint, 0.16)
  const baseDeep = darken(tint, 0.22)

  return (
    <Card
      elevation={0}
      onClick={handleExpandClick}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: { xs: 200, sm: 220 },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '20px',
        overflow: 'hidden',
        cursor: 'pointer',
        border: 'none',
        boxShadow: '0 6px 20px rgba(15, 23, 42, 0.09), 0 2px 6px rgba(15, 23, 42, 0.05)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12), 0 4px 10px rgba(15, 23, 42, 0.06)',
          '& .section-card-icon': { transform: 'scale(1.04)' }
        },
        '&:active': {
          transform: 'translateY(-1px)'
        }
      }}>
      <Box
        sx={{
          flex: '1 1 50%',
          minHeight: 0,
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(165deg, ${baseDeep} 0%, ${base} 42%, ${darken(tint, 0.19)} 100%)`,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(ellipse 95% 75% at 96% -8%, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.12) 28%, transparent 58%),
              radial-gradient(ellipse 55% 45% at 72% 8%, rgba(255,255,255,0.14) 0%, transparent 52%)
            `,
            pointerEvents: 'none',
            zIndex: 0
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.07) 100%)',
            pointerEvents: 'none',
            zIndex: 0
          }
        }}>
        <SectionTopSheen />
        <Box
          className="section-card-icon"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            transition: 'transform 0.3s ease',
            zIndex: 1
          }}>
          {imgSrc ? (
            <ProductImgStyle
              alt=""
              src={imgSrc}
              sx={{
                width: 'min(88%, 188px)',
                height: 'min(88%, 188px)',
                maxWidth: 188,
                maxHeight: 188,
                filter: 'brightness(0) invert(1) drop-shadow(0 1px 3px rgba(0,0,0,0.2))',
                opacity: 0.98
              }}
            />
          ) : (
            <Typography
              sx={{
                color: 'white',
                fontWeight: 800,
                fontSize: { xs: '2.25rem', sm: '2.65rem' },
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                textShadow: '0 1px 2px rgba(0,0,0,0.25)'
              }}>
              {initialsFromCode(code)}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flex: '1 1 50%',
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 2,
          py: 2,
          bgcolor: '#ffffff'
        }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: '0.94rem', sm: '1rem' },
            color: '#0f172a',
            lineHeight: 1.3,
            textAlign: 'left',
            flex: 1,
            wordBreak: 'break-word'
          }}>
          {i18n.t(code)}
        </Typography>
        <FaChevronRight
          aria-hidden
          size={18}
          color={darken(tint, 0.06)}
          style={{ flexShrink: 0, opacity: 0.9 }}
        />
      </Box>
    </Card>
  )
}

export default SectionCard
